
import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";

// Lazy load heavy components to reduce initial bundle size
const Media = React.lazy(() => import("./pages/Media"));
const MediaArticle = React.lazy(() => import("./pages/MediaArticle"));
import { generateSitemapXML, generateRobotsText } from "./utils/sitemapGenerator";

const queryClient = new QueryClient();

// Sitemap route handler
const SitemapRoute = () => {
  const [xml, setXml] = React.useState<string>('');
  
  React.useEffect(() => {
    generateSitemapXML().then(xmlContent => {
      setXml(xmlContent);
      
      // Set proper headers for XML content
      const metaTag = document.createElement('meta');
      metaTag.httpEquiv = 'content-type';
      metaTag.content = 'application/xml; charset=utf-8';
      document.head.appendChild(metaTag);
    });
  }, []);
  
  return (
    <div style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', padding: '20px' }}>
      {xml}
    </div>
  );
};

// Robots.txt route handler  
const RobotsRoute = () => {
  const [robotsText, setRobotsText] = React.useState<string>('');
  
  React.useEffect(() => {
    const content = generateRobotsText();
    setRobotsText(content);
    
    // Set proper headers for plain text content
    const metaTag = document.createElement('meta');
    metaTag.httpEquiv = 'content-type';
    metaTag.content = 'text/plain; charset=utf-8';
    document.head.appendChild(metaTag);
  }, []);
  
  return (
    <div style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', margin: 0, padding: '20px' }}>
      {robotsText}
    </div>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/media" element={
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <Media />
                </Suspense>
              } />
              <Route path="/media/:slug" element={
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <MediaArticle />
                </Suspense>
              } />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/sitemap.xml" element={<SitemapRoute />} />
              <Route path="/robots.txt" element={<RobotsRoute />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
